<script setup lang="ts">
import { computed } from 'vue';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/vue-3';
import { useApolloClient } from '@vue/apollo-composable';

import { UFileAttachment } from '../../UFileAttachment';
import type { FileAttachmentFile } from '../../UFileAttachment/types';
import { clearUpload, getUploadState } from './upload-registry';
import { resolveBinaryFileUrls } from './useBinaryFileHydration';

const props = defineProps<NodeViewProps>();

const { client } = useApolloClient();

const fileId = computed(() => props.node.attrs.fileId as string | null);
const uploadId = computed(() => props.node.attrs.uploadId as string | null);

const uploadState = computed(() => getUploadState(uploadId.value));

const hydrated = computed(() => (fileId.value ? resolveBinaryFileUrls(client, fileId.value) : null));

const isUploading = computed(() => !fileId.value && uploadState.value?.status === 'uploading');

/**
 * Error cases: the upload itself failed, the tab that started the upload was
 * closed before it finished (uploadId present but no registry entry), or the
 * file lookup failed after the fact.
 */
const isError = computed(() => {
  if (!fileId.value) {
    return !isUploading.value;
  }

  return hydrated.value?.status === 'error';
});

const errorMessage = computed(() => {
  if (uploadState.value?.errorMessage) {
    return uploadState.value.errorMessage;
  }

  if (!fileId.value && !isUploading.value) {
    return 'The upload was interrupted. Remove the file and try again.';
  }

  if (hydrated.value?.status === 'error') {
    return 'Failed to load the file.';
  }

  return '';
});

const file = computed<FileAttachmentFile>(() => ({
  url: hydrated.value?.url,
  downloadUrl: hydrated.value?.downloadUrl,
  meta: {
    name: props.node.attrs.name as string,
    size: props.node.attrs.size as number,
    mimeType: props.node.attrs.mimeType as string,
  },
}));

const onRemove = () => {
  if (uploadId.value) {
    clearUpload(uploadId.value);
  }

  props.deleteNode();
};
</script>

<template>
  <NodeViewWrapper
    class="my-2"
    data-testid="editor-attachment-file"
    :class="{ 'rounded-[10px] ring-2 ring-primary': props.selected && props.editor.isEditable }"
  >
    <UFileAttachment
      :file="file"
      :clearable="props.editor.isEditable"
      :uploading="isUploading"
      :error="isError"
      :error-message="errorMessage"
      @remove="onRemove"
    />
  </NodeViewWrapper>
</template>
